/**
 * Business data models with Zod schemas
 * Single source of truth pattern: Base schemas with derived variants
 */
import { formatISO, parseISO } from "date-fns";
import { z } from "zod";
import { isUsTimezone } from "@/shared/utils/timezones";

export const ReminderRuleSchema = z.object({
  id: z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i),
  minutes_before: z.number().int().min(1).max(2147483647),
}).strict();

const ReminderSchedulesSchema = z.array(ReminderRuleSchema).refine(
  (rules) => new Set(rules.map(rule => rule.id.toLowerCase())).size === rules.length &&
    new Set(rules.map(rule => rule.minutes_before)).size === rules.length,
  "Reminder rule IDs and offsets must be unique"
);

export const ReminderSettingsSchema = z.object({
  enabled: z.boolean(),
  schedules: ReminderSchedulesSchema,
  revision: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
}).strict();

export const UpdateReminderSettingsDtoSchema = z.object({
  enabled: z.boolean(),
  schedules: ReminderSchedulesSchema,
  expected_revision: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
}).strict();

export type ReminderRule = z.infer<typeof ReminderRuleSchema>;
export type ReminderSettings = z.infer<typeof ReminderSettingsSchema>;
export type UpdateReminderSettingsDto = z.infer<typeof UpdateReminderSettingsDtoSchema>;

export const UpdateReminderSettingsRequestSchema = UpdateReminderSettingsDtoSchema.transform((dto) => ({
  enabled: dto.enabled,
  schedules: dto.schedules,
  expectedRevision: dto.expected_revision,
}));

export type UpdateReminderSettingsRequest = z.infer<typeof UpdateReminderSettingsRequestSchema>;

export function toUpdateReminderSettingsDto(request: UpdateReminderSettingsRequest): UpdateReminderSettingsDto {
  return UpdateReminderSettingsDtoSchema.parse({
    enabled: request.enabled,
    schedules: request.schedules,
    expected_revision: request.expectedRevision,
  });
}

// ============================================================================
// BASE DTO SCHEMA - Single source of truth for snake_case fields
// ============================================================================
const BusinessDtoSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  modified_at: z.string(),
  name: z.string(),
  industry: z.string().nullable(),
  timezone: z.string(),
  currency: z.string(),
  settings: z.record(z.string(), z.unknown()),
  reminder_settings: ReminderSettingsSchema,
  deleted_at: z.string().nullable(),
  created_by: z.string().nullable(),
  owner_id: z.string().nullable(),
});

// Derived DTO schemas - automatically stay in sync with base
const BusinessTimezoneSchema = z.string().refine(isUsTimezone, "Choose a U.S. timezone.");

export const CreateBusinessDtoSchema = BusinessDtoSchema.extend({
  timezone: BusinessTimezoneSchema,
}).omit({
  reminder_settings: true,
  id: true,
  created_at: true,
  modified_at: true,
  deleted_at: true,
  created_by: true,
  owner_id: true,
}).partial({
  industry: true,
  settings: true,
});

export const UpdateBusinessDtoSchema = BusinessDtoSchema.extend({
  timezone: BusinessTimezoneSchema,
}).omit({
  reminder_settings: true,
  id: true,
  created_at: true,
  modified_at: true,
  deleted_at: true,
  created_by: true,
  owner_id: true,
}).partial();

// ============================================================================
// BIDIRECTIONAL TRANSFORMATIONS - Single source of truth
// ============================================================================

// Forward: DTO → Domain (snake_case + strings → camelCase + Dates)
export const BusinessSchema = BusinessDtoSchema.transform((dto) => ({
  id: dto.id,
  createdAt: parseISO(dto.created_at),
  modifiedAt: parseISO(dto.modified_at),
  name: dto.name,
  industry: dto.industry,
  timezone: dto.timezone,
  currency: dto.currency,
  settings: dto.settings,
  reminderSettings: dto.reminder_settings,
  deletedAt: dto.deleted_at ? parseISO(dto.deleted_at) : null,
  createdBy: dto.created_by,
  ownerId: dto.owner_id,
}));

// Reverse: Domain → DTO (camelCase + Dates → snake_case + strings)
export const BusinessToDtoSchema = BusinessSchema.transform((domain) => ({
  id: domain.id,
  created_at: formatISO(domain.createdAt),
  modified_at: formatISO(domain.modifiedAt),
  name: domain.name,
  industry: domain.industry,
  timezone: domain.timezone,
  currency: domain.currency,
  settings: domain.settings,
  reminder_settings: domain.reminderSettings,
  deleted_at: domain.deletedAt ? formatISO(domain.deletedAt) : null,
  created_by: domain.createdBy,
  owner_id: domain.ownerId,
}));

// Forward: CreateDTO → CreateRequest
export const CreateBusinessRequestSchema = CreateBusinessDtoSchema.transform(
  (dto) => ({
    name: dto.name,
    industry: dto.industry,
    timezone: dto.timezone,
    currency: dto.currency,
    settings: dto.settings,
  })
);

// Reverse: CreateRequest → CreateDTO
export const CreateBusinessRequestToDtoSchema =
  CreateBusinessRequestSchema.transform((domain) => ({
    name: domain.name,
    industry: domain.industry,
    timezone: domain.timezone,
    currency: domain.currency,
    settings: domain.settings,
  }));

// Forward: UpdateDTO → UpdateRequest
export const UpdateBusinessRequestSchema = UpdateBusinessDtoSchema.transform(
  (dto) => ({
    name: dto.name,
    industry: dto.industry,
    timezone: dto.timezone,
    currency: dto.currency,
    settings: dto.settings,
  })
);

// Reverse: UpdateRequest → UpdateDTO
export const UpdateBusinessRequestToDtoSchema =
  UpdateBusinessRequestSchema.transform((domain) => ({
    name: domain.name,
    industry: domain.industry,
    timezone: domain.timezone,
    currency: domain.currency,
    settings: domain.settings,
  }));

// Infer DTO types
export type BusinessDto = z.infer<typeof BusinessDtoSchema>;
export type CreateBusinessDto = z.infer<typeof CreateBusinessDtoSchema>;
export type UpdateBusinessDto = z.infer<typeof UpdateBusinessDtoSchema>;

// Infer domain types
export type Business = z.infer<typeof BusinessSchema>;
export type CreateBusinessRequest = z.infer<typeof CreateBusinessRequestSchema>;
export type UpdateBusinessRequest = z.infer<typeof UpdateBusinessRequestSchema>;

// ============================================================================
// CONVERSION UTILITIES
// ============================================================================

// DTO → Domain
export function fromBusinessDto(dto: unknown): Business {
  return BusinessSchema.parse(dto);
}

export function fromCreateBusinessDto(dto: unknown): CreateBusinessRequest {
  return CreateBusinessRequestSchema.parse(dto);
}

export function fromUpdateBusinessDto(dto: unknown): UpdateBusinessRequest {
  return UpdateBusinessRequestSchema.parse(dto);
}

// Domain → DTO
export function toBusinessDto(business: Business): BusinessDto {
  return BusinessToDtoSchema.parse(business);
}

export function toCreateBusinessDto(
  request: Partial<CreateBusinessRequest>
): CreateBusinessDto {
  return CreateBusinessRequestToDtoSchema.parse(request);
}

export function toUpdateBusinessDto(
  request: Partial<UpdateBusinessRequest>
): UpdateBusinessDto {
  return UpdateBusinessRequestToDtoSchema.parse(request);
}
